import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { PAGE_ROUTE, API_URL } from '../../shared/constant';
import { Form, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import LoaderComponent from '../../shared/loader';
import axios from 'axios';
import Crop from '../../shared/crop';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import moment from 'moment';

const PostsFormComponent = () => {
    const [imagePreview, setImagePreview] = useState('');
    const [isLoading, setLoading] = useState(false);
    const navigate = useNavigate();
    let { id } = useParams();
    const [error, setError] = useState([]);
    const inputRef = useRef(null);
    const [originalFeaturedImage, setOriginalFeaturedImage] = useState('');
    const [editDataLoaded, setEditDataLoaded] = useState(false);
    const [editDates, setEditDates] = useState({ created_at: null, updated_at: null });
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        excerpt: '',
        status: 'published',
        article_type: '',
        featured_image: '',
        category_id: '',
        meta_title: '',
        meta_description: '',
        meta_keywords: '',
        faqs: [{ question: '', answer: '' }, { question: '', answer: '' }, { question: '', answer: '' }],
        related_posts: '',
        is_compare: false
    });

    useEffect(() => {
        if (id) {
            setEditDataLoaded(false);
            onEdit();
        } else {
            setEditDataLoaded(false);
            setEditDates({ created_at: null, updated_at: null });
        }
    }, [id]);

    const onSubmit = (e) => {
        e.preventDefault();
        if (isInvalid(formData.title)) {
            toast.error("Title Is Required");
            return false;
        }
        if (isInvalid(formData.content)) {
            toast.error("Content Is Required");
            return false;
        }
        // Strip HTML tags for character count validation
        const textContent = formData.content.replace(/<[^>]*>/g, '').trim();
        if (textContent.length < 100) {
            toast.error("Content must be at least 100 characters");
            return false;
        }
        if (isInvalid(formData.status)) {
            toast.error("Status Is Required");
            return false;
        }

        setError([]);
        setLoading(true);
        let url = API_URL.ADMIN_POSTS_SAVE;
        let body = { ...formData };
        
        // Convert related_posts string to array if needed
        if (body.related_posts && typeof body.related_posts === 'string') {
            body.related_posts = body.related_posts.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
        }

        if (id) {
            body['id'] = parseInt(id);
            url = API_URL.ADMIN_POSTS_UPDATE;
            
            // Only include featured_image if it has been changed
            // If featured_image is the same as original or empty (and original was also empty), don't send it
            if (body.featured_image === originalFeaturedImage) {
                delete body.featured_image;
            }
            // If original had an image but new one is empty, send empty to remove it
            // If original was empty but new one has image, send it (already in body)
            // If both have images and they're different, send it (already in body)
        }

        axios.post(url, body).then((response) => {
            setLoading(false);
            if (response.data.status === 1) {
                if (id) {
                    toast.success('Post Updated Successfully', { autoClose: 3000 });
                } else {
                    toast.success('Post Created Successfully', { autoClose: 3000 });
                }
                setTimeout(() => {
                    navigate(PAGE_ROUTE.ADMIN_POSTS_LIST, { replace: true });
                }, 100);
            } else {
                if (response.data && response.data.data) {
                    let errors = [];
                    if (typeof response.data.data === 'object') {
                        Object.keys(response.data.data).forEach((key) => {
                            if (Array.isArray(response.data.data[key])) {
                                errors.push(...response.data.data[key]);
                            } else {
                                errors.push(response.data.data[key]);
                            }
                        });
                    } else {
                        errors.push(response.data.data);
                    }
                    setError(errors);
                    toast.error('Something went wrong..', { autoClose: 3000 });
                }
            }
        }, (error) => {
            setLoading(false);
            if (error.response && error.response.data && error.response.data.data) {
                let errors = [];
                if (typeof error.response.data.data === 'object') {
                    Object.keys(error.response.data.data).forEach((key) => {
                        if (Array.isArray(error.response.data.data[key])) {
                            errors.push(...error.response.data.data[key]);
                        } else {
                            errors.push(error.response.data.data[key]);
                        }
                    });
                } else {
                    errors.push(error.response.data.data);
                }
                setError(errors);
            }
            toast.error('Something went wrong..', { autoClose: 3000 });
        });
    };

    const isInvalid = (value) => {
        return (!value || value === '' || value == null);
    };

    const onEdit = () => {
        if (!id) return;
        axios.post(API_URL.ADMIN_POSTS_GET, { id: parseInt(id, 10) }).then((response) => {
            if (response.data.status === 1) {
                const data = response.data.data;
                const originalImage = data.featured_image || '';
                setOriginalFeaturedImage(originalImage);
                setFormData({
                    title: data.title || '',
                    content: data.content || '',
                    excerpt: data.excerpt || '',
                    status: data.status || 'draft',
                    article_type: data.article_type || '',
                    featured_image: originalImage,
                    category_id: data.category_id || '',
                    meta_title: data.meta_title || '',
                    meta_description: data.meta_description || '',
                    meta_keywords: data.meta_keywords || '',
                    faqs: data.faqs || [],
                    related_posts: Array.isArray(data.related_posts)
                        ? data.related_posts.join(',')
                        : (data.related_posts || ''),
                    is_compare: (data.is_compare === 1) ? true : false
                });
                if (data.featured_image_url) {
                    setImagePreview(data.featured_image_url);
                }
                setEditDates({
                    created_at: data.created_at || null,
                    updated_at: data.updated_at || null
                });
                setEditDataLoaded(true);
            }
        });
    };

    const handleFAQChange = (index, field, value) => {
        const newFaqs = [...formData.faqs];
        if (!newFaqs[index]) {
            newFaqs[index] = { question: '', answer: '' };
        }
        newFaqs[index][field] = value;
        setFormData(prev => ({
            ...prev,
            faqs: newFaqs
        }));
    };

    const addFAQ = () => {
        setFormData(prev => ({
            ...prev,
            faqs: [...prev.faqs, { question: '', answer: '' }]
        }));
    };

    const removeFAQ = (index) => {
        setFormData(prev => ({
            ...prev,
            faqs: prev.faqs.filter((_, i) => i !== index)
        }));
    };

    return (
        <div className="card">
            <div className="card-body">
                <h4 className="card-title">{id ? 'Edit' : 'Add'} Post</h4>
                <div className='text-end mt-3'>
                    {
                        isLoading && <LoaderComponent type={'watch'} height={35} width={35}></LoaderComponent>
                    }
                    <button type="submit" className="btn btn-primary">{id ? 'Update' : 'Create'}</button>
                    <NavLink to={PAGE_ROUTE.ADMIN_POSTS_LIST} className="btn btn-secondary">
                        Cancel
                    </NavLink>
                </div>
                {error.length > 0 &&
                    <>
                        {[
                            'danger',
                        ].map((variant) => (
                            <Alert key={variant} variant={variant}>
                                {error.map((element, key) => {
                                    return (
                                        <div key={key}>{element}</div>
                                    );
                                })}
                            </Alert>
                        ))}
                    </>
                }
                <form className="forms-sample" onSubmit={onSubmit}>
                    <div className='row'>
                        <div className='col-md-12'>
                            <div className="form-group">
                                <label htmlFor="title">Title <span className='text-danger'>*</span></label>
                                <input
                                    type="text"
                                    name='title'
                                    value={formData.title}
                                    className="form-control"
                                    id="title"
                                    onChange={(event) => {
                                        setFormData(prev => ({ ...prev, title: event.target.value }));
                                    }}
                                    required
                                />
                            </div>
                        </div>

                        <div className='col-md-12'>
                            <div className="form-group">
                                <label htmlFor="content">Content <span className='text-danger'>*</span> (min 100 characters)</label>
                                <CKEditor
                                    key={`ckeditor-${id || 'new'}-${editDataLoaded}`}
                                    editor={ClassicEditor}
                                    data={formData.content}
                                    onChange={(event, editor) => {
                                        const data = editor.getData();
                                        setFormData(prev => ({ ...prev, content: data }));
                                    }}
                                    config={{
                                        toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|', 'insertTable', '|', 'blockQuote', 'undo', 'redo'],
                                        table: {
                                            contentToolbar: [ 'tableColumn', 'tableRow', 'mergeTableCells' ]
                                        }
                                    }}
                                />
                                <small className="text-muted">
                                    {formData.content.replace(/<[^>]*>/g, '').trim().length} characters (HTML tags excluded)
                                </small>
                            </div>
                        </div>
                        <div className='col-md-12'>
                            <div className="form-group">
                                <label htmlFor="excerpt">Excerpt</label>
                                <textarea
                                    name='excerpt'
                                    value={formData.excerpt}
                                    className="form-control"
                                    id="excerpt"
                                    onChange={(event) => {
                                        setFormData(prev => ({ ...prev, excerpt: event.target.value }));
                                    }}
                                    rows="2"
                                    placeholder="Excerpt"
                                />
                                <small className="text-muted">
                                    {formData.excerpt.replace(/<[^>]*>/g, '').trim().length} characters (HTML tags excluded)
                                </small>
                            </div>
                        </div>

                        {id && (editDates.created_at || editDates.updated_at) && (
                            <div className='col-md-12'>
                                <div className="form-group mb-2">
                                    {editDates.created_at && (
                                        <small className="text-muted d-block">
                                            Created: {moment(editDates.created_at).format('lll')}
                                        </small>
                                    )}
                                    {editDates.updated_at && (
                                        <small className="text-muted d-block">
                                            Last updated: {moment(editDates.updated_at).format('lll')}
                                        </small>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className='col-md-4'>
                            <div className="form-group">
                                <label htmlFor="status">Status <span className='text-danger'>*</span></label>
                                <Form.Select
                                    aria-label="Status"
                                    value={formData.status}
                                    onChange={(event) => {
                                        setFormData(prev => ({ ...prev, status: event.target.value }));
                                    }}
                                    required
                                >
                                    <option value="draft">Draft</option>
                                    <option value="published">Published</option>
                                </Form.Select>
                            </div>
                        </div>

                        <div className='col-md-4'>
                            <div className="form-group">
                                <label htmlFor="article_type">Article Type</label>
                                <Form.Select
                                    aria-label="Article Type"
                                    value={formData.article_type}
                                    onChange={(event) => {
                                        setFormData(prev => ({ ...prev, article_type: event.target.value }));
                                    }}
                                >
                                    <option value="">Select Type</option>
                                    <option value="ai">AI</option>
                                    <option value="credit-score-debt">Credit Score & Debt</option>
                                    <option value="credit-cards-banking">Credit Cards & Banking</option>
                                    <option value="finance-statistics">Finance & Statistics</option>
                                    <option value="money">Money</option>
                                    <option value="wealth">Wealth</option>
                                    <option value="entrepreneur">Entrepreneur</option>
                                    <option value="business">Business</option>
                                    <option value="investing">Investing</option>
                                    <option value="leadership">Leadership</option>
                                    <option value="side-hustles">Side Hustles</option>
                                    <option value="retirement-planning">Retirement Planning</option>
                                    <option value="real-estate">Real Estate</option>
                                    <option value="quotes">Quotes</option>
                                </Form.Select>
                            </div>
                        </div>

                        <div className='col-md-4'>
                            <div className="form-group">
                                <label htmlFor="related_posts">Related Posts (comma-separated IDs)</label>
                                <input
                                    type="text"
                                    name='related_posts'
                                    value={formData.related_posts}
                                    className="form-control"
                                    id="related_posts"
                                    placeholder="e.g., 1, 2, 3"
                                    onChange={(event) => {
                                        setFormData(prev => ({ ...prev, related_posts: event.target.value }));
                                    }}
                                />
                            </div>
                        </div>

                        <div className='col-md-3'>
                            <div className='form-group'>
                                <label>Featured Image</label>
                                <Crop 
                                    imagePreview={imagePreview} 
                                    aspectRatio={16/9}
                                    cropShape='rect' 
                                    guid={''}
                                    onChangeCropper={async ({ fileCropped, fileCroppedBase64 }) => {
                                        setFormData(prev => ({ ...prev, featured_image: fileCroppedBase64 }));
                                        setImagePreview(fileCroppedBase64);
                                    }} 
                                />
                            </div>
                        </div>
                        <div className='col-md-4'>
                            <div className="form-group">
                                <div className="form-check mt-4">
                                    <input
                                        type="checkbox"
                                        name='is_compare'
                                        className="form-check-input"
                                        id="is_compare"
                                        checked={formData.is_compare}
                                        onChange={(event) => {
                                            setFormData(prev => ({ ...prev, is_compare: event.target.checked }));
                                        }}
                                    />
                                    <label className="form-check-label" htmlFor="is_compare">
                                        Enable Compare
                                    </label>
                                </div>
                            </div>
                        </div>

                        {id && formData.featured_image && (
                            <div className='col-md-12'>
                                <label>Preview</label>
                                <div>
                                    <img src={formData.featured_image} height={200} width={200} alt="featured" style={{ objectFit: 'cover' }} />
                                </div>
                            </div>
                        )}

                        <div className='col-md-12'>
                            <h5 className="mt-3 mb-3">SEO Fields</h5>
                        </div>

                        <div className='col-md-12'>
                            <div className="form-group">
                                <label htmlFor="meta_title">Meta Title</label>
                                <input
                                    type="text"
                                    name='meta_title'
                                    value={formData.meta_title}
                                    className="form-control"
                                    id="meta_title"
                                    onChange={(event) => {
                                        setFormData(prev => ({ ...prev, meta_title: event.target.value }));
                                    }}
                                />
                            </div>
                        </div>

                        <div className='col-md-12'>
                            <div className="form-group">
                                <label htmlFor="meta_description">Meta Description</label>
                                <textarea
                                    name='meta_description'
                                    value={formData.meta_description}
                                    className="form-control"
                                    id="meta_description"
                                    rows="2"
                                    onChange={(event) => {
                                        setFormData(prev => ({ ...prev, meta_description: event.target.value }));
                                    }}
                                />
                            </div>
                        </div>

                        <div className='col-md-12'>
                            <div className="form-group">
                                <label htmlFor="meta_keywords">Meta Keywords</label>
                                <input
                                    type="text"
                                    name='meta_keywords'
                                    value={formData.meta_keywords}
                                    className="form-control"
                                    id="meta_keywords"
                                    placeholder="comma-separated keywords"
                                    onChange={(event) => {
                                        setFormData(prev => ({ ...prev, meta_keywords: event.target.value }));
                                    }}
                                />
                            </div>
                        </div>

                        <div className='col-md-12'>
                            <h5 className="mt-3 mb-3 d-flex align-items-center flex-wrap gap-2">
                                FAQs
                                {id && (
                                    <NavLink to={PAGE_ROUTE.ADMIN_POSTS_FAQS + id} className="btn btn-outline-info btn-sm">
                                        Manage FAQs (API)
                                    </NavLink>
                                )}
                            </h5>
                            {formData.faqs.map((faq, index) => (
                                <div key={index} className="card mb-3">
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-11">
                                                <div className="form-group mb-2">
                                                    <label>Question</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Question"
                                                        value={faq.question || ''}
                                                        onChange={(e) => handleFAQChange(index, 'question', e.target.value)}
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label>Answer</label>
                                                    <textarea
                                                        className="form-control"
                                                        placeholder="Answer"
                                                        rows="2"
                                                        value={faq.answer || ''}
                                                        onChange={(e) => handleFAQChange(index, 'answer', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-1">
                                                <button
                                                    type="button"
                                                    className="btn btn-danger btn-sm mt-4"
                                                    onClick={() => removeFAQ(index)}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={addFAQ}
                            >
                                Add FAQ
                            </button>
                        </div>
                    </div>
                    <div className='text-end mt-3'>
                        {
                            isLoading && <LoaderComponent type={'watch'} height={35} width={35}></LoaderComponent>
                        }
                        <button type="submit" className="btn btn-primary">{id ? 'Update' : 'Create'}</button>
                        <NavLink to={PAGE_ROUTE.ADMIN_POSTS_LIST} className="btn btn-secondary">
                            Cancel
                        </NavLink>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PostsFormComponent;

